import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";

// Small local-only collapsible primitive. Persists open/closed per stable `id`
// in localStorage and responds to a page-wide "k9-collapse-all" event so a single
// global control can expand/collapse every mounted section coherently.
export const CollapsibleSection = ({
  id,
  title,
  icon: Icon,
  iconClass = "",
  badge,
  action,
  defaultOpen = true,
  card = false,
  cardClass = "rounded-2xl border border-border bg-card p-4",
  titleClass = "font-head text-xl font-bold",
  forceOpen = false,
  children,
  testid,
}) => {
  const storageKey = `k9-collapse:${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? defaultOpen : v === "1";
    } catch {
      return defaultOpen;
    }
  });
  const effectiveOpen = forceOpen || open;

  useEffect(() => {
    const onAll = (e) => {
      const next = !!(e.detail && e.detail.open);
      setOpen(next);
      try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
    };
    window.addEventListener("k9-collapse-all", onAll);
    return () => window.removeEventListener("k9-collapse-all", onAll);
  }, [storageKey]);

  const toggle = () =>
    setOpen((o) => {
      const n = !o;
      try { localStorage.setItem(storageKey, n ? "1" : "0"); } catch { /* ignore */ }
      return n;
    });

  const header = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        data-testid={testid ? `${testid}-toggle` : undefined}
        className="flex items-center gap-2 flex-1 min-w-0 text-left"
      >
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${effectiveOpen ? "" : "-rotate-90"}`} />
        {Icon && <Icon className={`h-5 w-5 shrink-0 ${iconClass}`} />}
        <span className={titleClass}>{title}</span>
        {badge}
      </button>
      {action}
    </div>
  );

  return (
    <section data-testid={testid} className={card ? cardClass : undefined}>
      {header}
      {effectiveOpen && <div className="mt-3">{children}</div>}
    </section>
  );
};
